import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { Icon, inkA, orange, PlainButton, tokens } from 'components/v3';
import { ASSET_ACCEPT } from '../hooks/useAssetUpload';

// An image slot for the Logo / Image field and a section's header banner:
// the uploaded picture, or a dashed placeholder. When `onPick` is given it
// carries an upload / replace button (a hidden file input behind a
// PlainButton). `raised` lifts the button above the field card's
// full-card select button.

const PickButton = styled(PlainButton)`
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 36px;
  padding: 0 12px;
  border-radius: 9px;
  font-size: 12.5px;
  font-weight: 700;
  color: ${orange(1)};
  background: ${orange(0.1)};
  border: 1px solid ${orange(0.35)};

  &:hover:not(:disabled) {
    background: ${orange(0.18)};
  }

  &:disabled {
    opacity: 0.6;
  }
`;

export const AssetPicker = ({ label, busy, onPick }) => {
  const inputRef = useRef(null);
  return (
    <>
      <PickButton
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          if (inputRef.current) inputRef.current.click();
        }}
      >
        <Icon name="upload" size={15} color="currentColor" />
        {busy ? 'Uploading…' : label}
      </PickButton>
      <input
        ref={inputRef}
        type="file"
        accept={ASSET_ACCEPT}
        aria-label={label}
        tabIndex={-1}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files && e.target.files[0];
          e.target.value = '';
          if (file) onPick(file);
        }}
      />
    </>
  );
};

AssetPicker.propTypes = {
  label: PropTypes.string.isRequired,
  busy: PropTypes.bool,
  onPick: PropTypes.func.isRequired,
};

AssetPicker.defaultProps = { busy: false };

const AssetSlot = ({
  url,
  width,
  height,
  placeholder,
  alt,
  busy,
  onPick,
  fit,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      style={{
        position: 'relative',
        width,
        maxWidth: '100%',
        height,
        boxSizing: 'border-box',
        borderRadius: 10,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        background: url ? inkA(theme, 0.04) : 'transparent',
        border: url
          ? '1px solid ' + t.border
          : '1.5px dashed ' + inkA(theme, 0.22),
        color: t.faint,
        fontSize: 12.5,
        textAlign: 'center',
      }}
    >
      {url ? (
        <img
          src={url}
          alt={alt}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: fit,
          }}
        />
      ) : (
        <>
          <Icon name="image" size={22} color="currentColor" />
          <span style={{ padding: '0 10px' }}>{placeholder}</span>
        </>
      )}
      {onPick ? (
        <div
          style={
            url ? { position: 'absolute', right: 8, bottom: 8 } : undefined
          }
        >
          <AssetPicker
            label={url ? 'Replace' : 'Upload image'}
            busy={busy}
            onPick={onPick}
          />
        </div>
      ) : null}
    </div>
  );
};

AssetSlot.propTypes = {
  url: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  height: PropTypes.number.isRequired,
  placeholder: PropTypes.string.isRequired,
  alt: PropTypes.string,
  busy: PropTypes.bool,
  onPick: PropTypes.func,
  fit: PropTypes.oneOf(['contain', 'cover']),
};

AssetSlot.defaultProps = {
  url: null,
  alt: '',
  busy: false,
  onPick: undefined,
  fit: 'contain',
};

export default AssetSlot;
